import role4Schema from '../../../../assets/forms/role-120000.form.json';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import {
  buildWarningBag,
  sheetHasTriggeredWarning,
  WarnableControl,
} from './warning-eval.helper';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { exceedsSchemaMaxLength } from '../../../utils/helpers';
import {
  getVisibleDirectorCount,
  ROLE4_DIRECTOR_MAPPINGS,
  ROLE4_IDENTIFICATION_PATTERN,
  role4TypeRequiresPattern,
} from './role4-directors-report.rules';

/**
 * Pure validator for Role 4 (Directors Report).
 *
 * Mirrors the editor's role4-120000-directors-report.component.ts validation
 * surface:
 *
 *  - Static required fields from schema (control.required === true) — only
 *    counted for fields that are visible (no directorNumber set, OR
 *    directorNumber <= visible count).
 *  - Per-director: when visible, Name and TypeOfIdentification are required.
 *  - Per-director: when visible AND type in {MyKad, MyPR, MyKAS},
 *    IdentificationNumber requires the 12-digit pattern. Otherwise just
 *    required (when visible).
 *
 * Director visibility is driven by ssmt:NumberOfDirectorsSigningDirectorsReport,
 * which resolves to ZERO when absent/blank (`getVisibleDirectorCount`) — the same
 * fallback the component and the submit projector use.
 */

interface SchemaControl {
  qname: string;
  required?: boolean;
  ui?: { directorNumber?: number };
}

const SCHEMA_CONTROLS: ReadonlyArray<SchemaControl> = role4Schema.controls as SchemaControl[];

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function checkStaticAndDirectorRequiredErrors(
  sheetData: Record<string, any>,
  visibleDirectorCount: number
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const control of SCHEMA_CONTROLS) {
    const directorNumber = control.ui?.directorNumber;
    const isVisible =
      directorNumber === undefined || directorNumber <= visibleDirectorCount;

    // Director-specific Name / TypeOfIdentification fields are required
    // when their director is visible, regardless of the schema's required
    // flag (matches updateDirectorFieldValidators in the component).
    const isDirectorNameOrType =
      directorNumber !== undefined &&
      (control.qname.includes('Name') ||
        control.qname.includes('TypeOfIdentification'));

    const required =
      isVisible && (control.required === true || isDirectorNameOrType);

    if (required && isEmpty(sheetData[control.qname])) {
      errors[control.qname] = `${control.qname} is required`;
      continue;
    }

    // Schema-declared 255-character ceiling on text controls, scoped by the
    // same visibility rule as `required` — a hidden director contributes
    // nothing to the verdict. Shares `exceedsSchemaMaxLength` with the editor.
    if (isVisible && exceedsSchemaMaxLength(sheetData[control.qname], control.qname, role4Schema)) {
      errors[control.qname] = `${control.qname} exceeds its maximum length`;
    }
  }

  return errors;
}

function checkDirectorIdentificationErrors(
  sheetData: Record<string, any>,
  visibleDirectorCount: number
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const mapping of ROLE4_DIRECTOR_MAPPINGS) {
    const isVisible = mapping.directorNumber <= visibleDirectorCount;
    if (!isVisible) continue;

    const typeValue = sheetData[mapping.typeField];
    const idValue = sheetData[mapping.idField];

    const requiresPattern = role4TypeRequiresPattern(typeValue);

    if (isEmpty(idValue)) {
      // Always required when director is visible — the component flips
      // between [required, pattern] and [required] in applyConditionalValidator
      // but `required` is present in both visible cases.
      errors[mapping.idField] = `${mapping.idField} is required`;
      continue;
    }

    if (requiresPattern && !ROLE4_IDENTIFICATION_PATTERN.test(String(idValue))) {
      errors[mapping.idField] = `${mapping.idField} must be a 12-digit number`;
      continue;
    }

    // Free-text identification: the 12-digit pattern does not apply, so the
    // schema's 255-character ceiling is the only rule left to enforce.
    if (exceedsSchemaMaxLength(idValue, mapping.idField, role4Schema)) {
      errors[mapping.idField] = `${mapping.idField} exceeds its maximum length`;
    }
  }

  return errors;
}

export const role4DirectorsReportValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.directorsReport,
  // Tier 1 by primary characteristic. Director-visibility logic is encoded
  // as a small, deterministic projection from one same-sheet field
  // (NumberOfDirectorsSigningDirectorsReport), not a true cross-sheet rule.
  tier: 1,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const visibleDirectorCount = getVisibleDirectorCount(sheetData);
    const errors: Record<string, string> = {
      ...checkStaticAndDirectorRequiredErrors(sheetData, visibleDirectorCount),
      ...checkDirectorIdentificationErrors(sheetData, visibleDirectorCount),
    };
    return {
      hasErrors: Object.keys(errors).length > 0,
      hasSuggestedChanges: sheetHasTriggeredWarning(
        role4Schema.controls as unknown as WarnableControl[],
        sheetData,
        buildWarningBag(
          context.crossSheetData?.get(SHEET_KEYS.filingInfo) ??
            context.crossSheetData?.get(SHEET_KEYS.filingPrerequisites) ??
            {},
          sheetData,
          // Directors' report compares its signing date against auditorsReport;
          // nest the cross-sheet map + own key so that warning resolves here too.
          { sheets: context.crossSheetData, sheetKey: SHEET_KEYS.directorsReport }
        )
      ),
      errors,
    };
  },
};
